import type { AxiosRequestConfig } from 'axios';
import type {
  CreateLeadCaptureSessionRequest,
  FormAnswer,
  LeadCaptureOptionsResponse,
  LeadCapturePublicInfo,
  LeadCapturePublicSubmitRequest,
  LeadCapturePublicSubmitResponse,
  LeadCaptureSession,
  LeadCaptureSessionResponse,
} from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * Suprime o `toast.error` genérico do interceptor global (`src/lib/api.ts`)
 * em `getPublic`/`submitPublic`/`submitForm` — quem trata o erro é a própria
 * tela: a página pública (`LeadCapturePublicPage`) cobre 404/410/422/429 com
 * `capture-public-error`/estado `expired`, e a etapa "Formulário"
 * (`CaptureFormStep`) mostra a mensagem da API (ex.: 422 dizendo QUAL campo
 * obrigatório falta) embaixo da pergunta. Sem isso vinha um toast vermelho
 * genérico por cima da tela dedicada — e, no formulário, duplicado com o erro
 * inline. Mesmo padrão de `checklistsApi`.
 */
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

/** Cliente HTTP da captura por QR (`/api/leads/capture/*`). Todos devolvem `response.data.data`. */
export const captureApi = {
  async getOptions(): Promise<LeadCaptureOptionsResponse> {
    const response = await api.get('/leads/capture/options');
    return response.data.data;
  },
  async createSession(body: CreateLeadCaptureSessionRequest): Promise<LeadCaptureSessionResponse> {
    const response = await api.post('/leads/capture/sessions', body);
    return response.data.data;
  },
  async getSession(uuid: string): Promise<LeadCaptureSessionResponse> {
    const response = await api.get(`/leads/capture/sessions/${uuid}`);
    return response.data.data;
  },
  async cancel(uuid: string): Promise<LeadCaptureSession> {
    const response = await api.post(`/leads/capture/sessions/${uuid}/cancel`);
    return response.data.data;
  },
  async skipForm(uuid: string): Promise<LeadCaptureSession> {
    const response = await api.post(`/leads/capture/sessions/${uuid}/skip-form`);
    return response.data.data;
  },
  async complete(uuid: string): Promise<LeadCaptureSession> {
    const response = await api.post(`/leads/capture/sessions/${uuid}/complete`);
    return response.data.data;
  },
  async submitForm(uuid: string, answers: FormAnswer[]): Promise<LeadCaptureSession> {
    const response = await api.post(`/leads/capture/sessions/${uuid}/form`, { answers }, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },
  // Públicas — sem token (o interceptor só põe Authorization se existir token)
  async getPublic(uuid: string): Promise<LeadCapturePublicInfo> {
    const response = await api.get(`/leads/capture/public/${uuid}`, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },
  async submitPublic(uuid: string, body: LeadCapturePublicSubmitRequest): Promise<LeadCapturePublicSubmitResponse> {
    const response = await api.post(`/leads/capture/public/${uuid}/submit`, body, HANDLE_ERROR_LOCALLY);
    return response.data.data;
  },
};
